import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useUserState } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import { useEventTracking, useSessionReplayCapture } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { useSendPaymentRequest, useStripeConnect } from '../store/hooks';
import type { PatientPaymentMethod } from '../types';
import {
  formDataToRequest,
  hasRequiredFields,
  type PaymentRequestFormData,
} from '../utils/validation';

export interface RequestPaymentOptions {
  amount?: string;
  patientId?: string;
  patientName?: string;
  reason?: string;
  dueDate?: string;
  appointmentId?: string;
  encounterId?: string;
  actionId?: string;
  source?: string;
  patientTelephone?: string;
  hasPaymentMethod?: boolean;
  defaultPaymentMethod?: PatientPaymentMethod;
  paymentMethods?: PatientPaymentMethod[];
  onSuccess?: () => void;
}

interface RequestPaymentState {
  isLoading: boolean;
  canRequest: boolean;
  triggerPaymentRequest: () => Promise<void>;
}

/**
 * Headless hook for requesting payments from patients
 *
 * This hook encapsulates all the business logic for payment requests:
 * - SMS onboarding validation
 * - Stripe Connect verification
 * - Modal orchestration
 * - Payment request sending
 * - Event tracking
 *
 * @param options Payment request configuration
 * @returns State and trigger function for payment requests
 */
export function useRequestPayment(options: RequestPaymentOptions = {}): RequestPaymentState {
  const {
    amount,
    patientId,
    patientName,
    reason,
    dueDate,
    appointmentId,
    encounterId,
    actionId,
    source = 'unknown',
    patientTelephone,
    hasPaymentMethod,
    defaultPaymentMethod,
    paymentMethods,
    onSuccess,
  } = options;

  const { enqueueSnackbar } = useSnackbar();
  const { open: showModal } = useModal();
  const { hasStripeAccount } = useStripeConnect();
  const { mutate: sendPaymentRequest, isLoading } = useSendPaymentRequest();
  const { startCapture } = useSessionReplayCapture();
  const { payment, smsOnboarding, stripeConnect } = useEventTracking();
  const userState = useUserState();
  const location = useLocation();
  const navigate = useNavigate();
  // TODO: when this get's moved to a query hook, we can skip if telephone and name are provided... or better yet remove phones and names from this hook entirely
  const { patient } = usePatientById(patientId);
  const patientTelephoneFinal = patientTelephone || patient?.mobile_phone || undefined;
  const patientNameFinal =
    patientName || (patient ? `${patient.first_name} ${patient.last_name}` : undefined);

  const isSMSOnboarded = userState?.onboardingSteps.patient_communication_sms_onboarding_completed;
  // Always allow requesting payment - if no patient ID, the modal will show PatientPicker
  const canRequest = true;

  const triggerPaymentRequest = useCallback(async () => {
    // Start session replay capture
    startCapture('payment_request_triggered');

    // Track payment request initiation
    payment.requestInitiated({
      source,
      amount: amount ? parseFloat(amount) : undefined,
    });

    // Check SMS onboarding first
    if (!isSMSOnboarded) {
      // Track SMS onboarding initiation
      smsOnboarding.initiated({ source: 'payment_request' });

      // Open SMS onboarding modal with continuation action
      showModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {
        continuationAction: !hasStripeAccount
          ? {
              type: 'modals/pushModal',
              payload: {
                modalType: MODAL_TYPES.STRIPE_CONNECT_INFO,
                modalProps: {
                  returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
                  // Note: onConnectComplete callback removed - it's non-serializable and the user
                  // gets redirected to Stripe anyway, so the snackbar wouldn't be visible
                },
              },
            }
          : {
              type: 'modals/pushModal',
              payload: {
                modalType: MODAL_TYPES.PAYMENT_REQUEST,
                modalProps: {
                  amount,
                  patientId,
                  patientName: patientNameFinal,
                  reason,
                  dueDate,
                  appointmentId,
                  encounterId,
                  actionId,
                  patientTelephone: patientTelephoneFinal,
                  hasPaymentMethod,
                  defaultPaymentMethod,
                  paymentMethods,
                  source,
                },
              },
            },
      });
      return;
    }

    // Check if Stripe is connected
    if (!hasStripeAccount) {
      // Track Stripe Connect initiation
      stripeConnect.initiated({ source: 'payment_request' });

      // Open Stripe Connect modal
      // Note: onConnectComplete callback removed - it's non-serializable and the user
      // gets redirected to Stripe anyway, so the snackbar wouldn't be visible
      showModal(MODAL_TYPES.STRIPE_CONNECT_INFO, {
        returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
      });
      return;
    }

    // Prepare form data
    const formData: Partial<PaymentRequestFormData> = {
      amount,
      patientId,
      reason,
      dueDate,
      appointmentId,
      encounterId,
      actionId,
      source,
    };

    // Check if all required fields are present (including patientId)
    // Only send directly if we have all required fields
    if (patientId && hasRequiredFields(formData)) {
      // Send payment request directly
      try {
        const request = formDataToRequest(formData as PaymentRequestFormData);
        await sendPaymentRequest(request, {
          onSuccess: () => {
            // Track successful payment request
            payment.requestSent({
              source,
              amount: amount ? parseFloat(amount) : undefined,
              delivery_method: 'sms',
            });
            enqueueSnackbar('Payment request sent successfully', { variant: 'success' });
            onSuccess?.();
          },
          onError: () => {
            // Track failed payment request
            payment.requestFailed({
              source,
              error_type: 'send_failed',
              error_message: 'Failed to send payment request',
            });
            enqueueSnackbar('Failed to send payment request', { variant: 'error' });
          },
        });
      } catch (error) {
        console.error('Error sending payment request:', error);
        // Track error
        payment.requestFailed({
          source,
          error_type: 'exception',
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } else {
      // Track modal opening
      payment.modalOpened({ source });

      // Open payment request modal with pre-filled data
      // If no patientId, the modal will show the PatientPicker
      showModal(MODAL_TYPES.PAYMENT_REQUEST, {
        amount,
        patientId,
        patientName: patientNameFinal,
        reason,
        dueDate,
        appointmentId,
        encounterId,
        actionId,
        patientTelephone: patientTelephoneFinal,
        hasPaymentMethod,
        defaultPaymentMethod,
        paymentMethods,
        source,
      });
    }
  }, [
    isSMSOnboarded,
    hasStripeAccount,
    amount,
    patientId,
    patientNameFinal,
    reason,
    dueDate,
    appointmentId,
    encounterId,
    actionId,
    source,
    patientTelephoneFinal,
    hasPaymentMethod,
    defaultPaymentMethod,
    paymentMethods,
    showModal,
    sendPaymentRequest,
    enqueueSnackbar,
    onSuccess,
    startCapture,
    payment,
    smsOnboarding,
    stripeConnect,
  ]);

  // Check for query parameter on mount to auto-open modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenPaymentRequest = params.get('openPaymentRequest') === 'true';

    if (shouldOpenPaymentRequest && hasStripeAccount && isSMSOnboarded) {
      // Track successful Stripe Connect completion
      stripeConnect.completed({});

      // Track modal opening after Stripe connection
      payment.modalOpened({ source: 'stripe_return' });

      // Open payment request modal
      showModal(MODAL_TYPES.PAYMENT_REQUEST, {
        amount,
        patientId,
        patientName: patientNameFinal,
        reason,
        dueDate,
        appointmentId,
        encounterId,
        actionId,
        patientTelephone: patientTelephoneFinal,
        hasPaymentMethod,
        defaultPaymentMethod,
        paymentMethods,
        source,
      });

      // Clean up the URL after opening the modal
      params.delete('openPaymentRequest');
      const newSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, showModal, hasStripeAccount, isSMSOnboarded]);

  return {
    isLoading,
    canRequest,
    triggerPaymentRequest,
  };
}
